/**
 * Phase 7C-4 -- one typed error for the whole auth flow.
 *
 * Every Axios failure from `POST /api/v1/auth/session` is mapped into an
 * `AuthApiError` HERE and nowhere else, so screens branch on
 * `isUnauthenticated` / `isSessionConflict` / `isRateLimited` instead of
 * on status numbers or on Thai message text (which is written for a
 * person and may change).
 *
 * WHAT IS DELIBERATELY NOT ON THIS ERROR
 * ----------------------------------------------------------------------
 * The Axios error carries the request config, and the request config
 * carries the `Authorization` header (or the password, for
 * SESSION_CREATE). None of that is copied onto `AuthApiError`: it holds
 * the status, the Backend's machine code, the message, the wait time and
 * the safe 409 metadata -- values that can be shown, logged or put in a
 * test assertion without leaking a credential.
 */

/**
 * The 409 metadata the Backend chooses to disclose about somebody
 * else's session (`_conflict_public_view` in `sessionService.py`).
 * Strictly these four fields: no `session_id`, no `device_id`, no token.
 * Even if a future Backend added more, the Website only reads these.
 */
export interface ActiveSessionInfo {
    device_label: string | null;
    client_type: string | null;
    absolute_expires_at: string | null;
    idle_expires_at: string | null;
}

export const GENERIC_CREDENTIAL_MESSAGE = 'รหัสผู้ใช้ หรือรหัสผ่านไม่ถูกต้อง';
export const GENERIC_CONNECTION_MESSAGE = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง';
export const GENERIC_SERVER_MESSAGE = 'เซิร์ฟเวอร์ขัดข้อง กรุณาลองใหม่อีกครั้ง';
export const GENERIC_FORBIDDEN_MESSAGE = 'บัญชีนี้ไม่มีสิทธิ์ใช้งานส่วนนี้';
export const GENERIC_RATE_LIMIT_MESSAGE = 'มีการพยายามเข้าสู่ระบบมากเกินไป กรุณารอสักครู่';
export const GENERIC_BAD_REQUEST_MESSAGE = 'ข้อมูลที่ส่งมาไม่ถูกต้อง';
export const MALFORMED_RESPONSE_MESSAGE = 'รูปแบบข้อมูลจากเซิร์ฟเวอร์ไม่ถูกต้อง';

export interface AuthApiErrorOptions {
    status?: number | null;
    code?: string | null;
    retryAfterSeconds?: number | null;
    activeSession?: ActiveSessionInfo | null;
    isNetwork?: boolean;
}

export class AuthApiError extends Error {
    readonly status: number | null;
    readonly code: string | null;
    /** Seconds the Backend asked us to wait (429 only), or null. */
    readonly retryAfterSeconds: number | null;
    /** Safe description of the blocking session (409 only), or null. */
    readonly activeSession: ActiveSessionInfo | null;
    /** True when no HTTP response arrived at all (offline, DNS, timeout). */
    readonly isNetwork: boolean;

    constructor(message: string, options: AuthApiErrorOptions = {}) {
        super(message);
        // Required for `instanceof` to work when the build targets ES5.
        Object.setPrototypeOf(this, AuthApiError.prototype);
        this.name = 'AuthApiError';
        this.status = options.status ?? null;
        this.code = options.code ?? null;
        this.retryAfterSeconds = options.retryAfterSeconds ?? null;
        this.activeSession = options.activeSession ?? null;
        this.isNetwork = options.isNetwork ?? false;
    }

    /** 401: no usable session, or wrong credentials on SESSION_CREATE. */
    get isUnauthenticated(): boolean { return this.status === 401; }
    /** 403: the caller IS known and is not allowed. Never a sign-out. */
    get isForbidden(): boolean { return this.status === 403; }
    /** 409: correct password, but a live session exists on another device. */
    get isSessionConflict(): boolean { return this.status === 409; }
    /** 429: slow down; see `retryAfterSeconds`. */
    get isRateLimited(): boolean { return this.status === 429; }
    /** 400: the request itself was malformed. */
    get isBadRequest(): boolean { return this.status === 400; }
    /** 5xx: the Backend failed; the session may still be perfectly valid. */
    get isServerError(): boolean { return this.status !== null && this.status >= 500; }
}

type HeaderBag = Record<string, unknown> | { get?: (name: string) => unknown } | null | undefined;

/**
 * Reads one response header regardless of how Axios shaped the bag
 * (an `AxiosHeaders` instance with `.get`, or a plain lower-cased object).
 */
export const readHeader = (headers: HeaderBag, name: string): string | null => {
    if (!headers) return null;
    const wanted = name.toLowerCase();
    try {
        const getter = (headers as { get?: (n: string) => unknown }).get;
        if (typeof getter === 'function') {
            const viaGet = getter.call(headers, name);
            if (viaGet !== undefined && viaGet !== null) return String(viaGet);
        }
        const bag = headers as Record<string, unknown>;
        for (const key of Object.keys(bag)) {
            if (key.toLowerCase() === wanted) {
                const value = bag[key];
                if (value === undefined || value === null) return null;
                return String(value);
            }
        }
    } catch (e) {
        return null;
    }
    return null;
};

const nonNegativeInt = (raw: unknown): number | null => {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : null;
    }
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!/^\d+$/.test(trimmed)) return null;
        const parsed = parseInt(trimmed, 10);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    }
    return null;
};

/**
 * `Retry-After` in seconds: the header first, the body's
 * `retry_after_seconds` second.
 *
 * The body fallback is not decoration. `Retry-After` is not a
 * CORS-safelisted response header, and Staging's CORS configuration sets
 * no `Access-Control-Expose-Headers`, so a browser hides the header from
 * page script even though `curl` can see it. Until the Backend exposes
 * it, the body value is the one the Website actually receives.
 *
 * RFC 9110 also allows an HTTP-date; jinda-pos-api sends a plain
 * integer, so anything else is reported as null and the caller applies
 * its own documented fallback instead of waiting on a nonsense value.
 */
export const parseRetryAfter = (headers: HeaderBag, body: unknown): number | null => {
    const fromHeader = nonNegativeInt(readHeader(headers, 'Retry-After'));
    if (fromHeader !== null) return fromHeader;
    if (body && typeof body === 'object') {
        const fromBody = nonNegativeInt((body as Record<string, unknown>)['retry_after_seconds']);
        if (fromBody !== null) return fromBody;
    }
    return null;
};

const asText = (value: unknown): string | null => {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

const pickActiveSession = (body: unknown): ActiveSessionInfo | null => {
    if (!body || typeof body !== 'object') return null;
    const raw = (body as Record<string, unknown>)['active_session'];
    if (!raw || typeof raw !== 'object') return null;
    const record = raw as Record<string, unknown>;
    return {
        device_label: asText(record['device_label']),
        client_type: asText(record['client_type']),
        absolute_expires_at: asText(record['absolute_expires_at']),
        idle_expires_at: asText(record['idle_expires_at']),
    };
};

const parseBody = (data: unknown): Record<string, unknown> | null => {
    if (data && typeof data === 'object') return data as Record<string, unknown>;
    if (typeof data === 'string' && data.trim().length > 0) {
        try {
            const decoded = JSON.parse(data);
            return decoded && typeof decoded === 'object' ? decoded : null;
        } catch (e) {
            return null;
        }
    }
    return null;
};

const defaultMessageFor = (status: number): string => {
    if (status === 401) return GENERIC_CREDENTIAL_MESSAGE;
    if (status === 403) return GENERIC_FORBIDDEN_MESSAGE;
    if (status === 429) return GENERIC_RATE_LIMIT_MESSAGE;
    if (status >= 500) return GENERIC_SERVER_MESSAGE;
    return GENERIC_BAD_REQUEST_MESSAGE;
};

/**
 * Maps anything thrown by an Axios call into an `AuthApiError`.
 *
 * Never throws itself, and never copies the request config -- see the
 * module docstring.
 */
export const toAuthApiError = (error: unknown): AuthApiError => {
    if (error instanceof AuthApiError) return error;

    const candidate = (error ?? {}) as { response?: { status?: unknown; data?: unknown; headers?: HeaderBag } };
    const response = candidate.response;

    if (!response || response.status === undefined || response.status === null) {
        return new AuthApiError(GENERIC_CONNECTION_MESSAGE, { status: null, isNetwork: true });
    }

    const status = Number(response.status);
    if (!Number.isFinite(status)) {
        return new AuthApiError(GENERIC_CONNECTION_MESSAGE, { status: null, isNetwork: true });
    }

    const body = parseBody(response.data);
    const message = asText(body?.['message']) ?? defaultMessageFor(status);
    const code = asText(body?.['code']);

    return new AuthApiError(message, {
        status,
        code,
        retryAfterSeconds: status === 429 ? parseRetryAfter(response.headers, body) : null,
        activeSession: status === 409 ? pickActiveSession(body) : null,
        isNetwork: false,
    });
};
